import useEmpresa from '@/contexts/useEmpresa'
import {
    formaPagamentoPadrao,
    getAcrescimoRepasseTaxas,
    getTaxasMetodoPagamento,
    getTotalBrutoFaturas,
    getTotalDescontoAntecipadoFaturas,
    getTotalJurosFaturas,
    getTotalLiquidoFaturas,
    getTotalMultaFaturas,
    type MetodoPagamento,
} from '@/services/fatura'
import type { Fatura } from '@/types/fatura'
import { formatMoney } from '@/utils'

type FaturaValorResumoVariant = 'panel' | 'compact' | 'total-only'

interface FaturaValorResumoProps {
    faturas: Fatura[]
    variant?: FaturaValorResumoVariant
    className?: string
    metodo?: MetodoPagamento | ''
    incluirRepasse?: boolean
}

export default function FaturaValorResumo({
    faturas,
    variant = 'panel',
    className,
    metodo,
    incluirRepasse = false,
}: FaturaValorResumoProps) {
    const { empresa } = useEmpresa()
    const taxas = getTaxasMetodoPagamento(empresa, metodo || formaPagamentoPadrao(empresa))
    const valorBruto = getTotalBrutoFaturas(faturas)
    const descontoAntecipado = getTotalDescontoAntecipadoFaturas(faturas)
    const multa = getTotalMultaFaturas(faturas, taxas)
    const juros = getTotalJurosFaturas(faturas, taxas)
    const subtotal = valorBruto - descontoAntecipado + multa + juros
    const taxaRepasse = incluirRepasse ? getAcrescimoRepasseTaxas(subtotal, taxas) : 0
    const valorLiquido = incluirRepasse
        ? getTotalLiquidoFaturas(faturas, new Date(), taxas)
        : subtotal
    const temDesconto = descontoAntecipado > 0
    const temAtraso = multa > 0 || juros > 0
    const temAcrescimo = temAtraso || taxaRepasse > 0
    const temBreakdown = temDesconto || temAcrescimo

    const classes = ['fatura-valor-resumo', `fatura-valor-resumo--${variant}`, className]
        .filter(Boolean)
        .join(' ')

    if (variant === 'total-only') {
        return (
            <div className={classes}>
                <strong className="fatura-valor-resumo-total-only">{formatMoney(valorLiquido)}</strong>
            </div>
        )
    }

    if (!temBreakdown) {
        return (
            <div className={classes}>
                <div className="fatura-valor-resumo-row is-total is-single">
                    <span className="fatura-valor-resumo-label">
                        {variant === 'compact' ? 'TOTAL' : 'Valor total'}
                    </span>
                    <strong className="fatura-valor-resumo-valor">{formatMoney(valorBruto)}</strong>
                </div>
            </div>
        )
    }

    const linhasAcrescimo = (
        <>
            {multa > 0 && (
                <div className={`fatura-valor-resumo-row is-acrescimo${variant === 'compact' ? ' is-compact' : ''}`}>
                    <span className="fatura-valor-resumo-label">Multa</span>
                    <span className="fatura-valor-resumo-valor">+ {formatMoney(multa)}</span>
                </div>
            )}
            {juros > 0 && (
                <div className={`fatura-valor-resumo-row is-acrescimo${variant === 'compact' ? ' is-compact' : ''}`}>
                    <span className="fatura-valor-resumo-label">Juros</span>
                    <span className="fatura-valor-resumo-valor">+ {formatMoney(juros)}</span>
                </div>
            )}
            {taxaRepasse > 0 && (
                <div className={`fatura-valor-resumo-row is-acrescimo${variant === 'compact' ? ' is-compact' : ''}`}>
                    <span className="fatura-valor-resumo-label">Taxa</span>
                    <span className="fatura-valor-resumo-valor">+ {formatMoney(taxaRepasse)}</span>
                </div>
            )}
        </>
    )

    if (variant === 'compact') {
        return (
            <div className={classes}>
                <div className="fatura-valor-resumo-row is-subtotal">
                    <span className="fatura-valor-resumo-label">Valor total</span>
                    <span className="fatura-valor-resumo-valor">{formatMoney(valorBruto)}</span>
                </div>
                {temDesconto && (
                    <div className="fatura-valor-resumo-row is-desconto is-compact">
                        <span className="fatura-valor-resumo-label">Desconto</span>
                        <span className="fatura-valor-resumo-valor">- {formatMoney(descontoAntecipado)}</span>
                    </div>
                )}
                {linhasAcrescimo}
                <div className="fatura-valor-resumo-row is-total is-final">
                    <span className="fatura-valor-resumo-label">TOTAL</span>
                    <strong className="fatura-valor-resumo-valor">{formatMoney(valorLiquido)}</strong>
                </div>
            </div>
        )
    }

    return (
        <div className={classes}>
            <div className="fatura-valor-resumo-row">
                <span className="fatura-valor-resumo-label">Valor total</span>
                <span className="fatura-valor-resumo-valor">{formatMoney(valorBruto)}</span>
            </div>

            {temDesconto && (
                <div className="fatura-valor-resumo-row is-desconto">
                    <span className="fatura-valor-resumo-label">Desconto</span>
                    <span className="fatura-valor-resumo-valor">- {formatMoney(descontoAntecipado)}</span>
                </div>
            )}

            {linhasAcrescimo}

            <div className="fatura-valor-resumo-row is-total">
                <span className="fatura-valor-resumo-label">Total a pagar</span>
                <strong className="fatura-valor-resumo-valor is-liquido">{formatMoney(valorLiquido)}</strong>
            </div>
            {temDesconto && !temAtraso && (
                <p className="fatura-valor-resumo-note">Válido pagando até o vencimento</p>
            )}
            {temAtraso && (
                <p className="fatura-valor-resumo-note is-acrescimo">Inclui multa e juros por atraso</p>
            )}
        </div>
    )
}
